<template>
  <div
    class="flex w-[185px] flex-col gap-2 rounded-sm border border-divider p-2-5 shadow-main"
    :class="
      communicationToolOptions.showAsPopover
        ? 'bg-gray-100 hover:bg-gray-200'
        : 'bg-white hover:bg-gray-50'
    "
  >
    <div
      v-if="communicationToolOptions.showAsPopover"
      class="flex flex-col space-y-1 text-xs"
    >
      <p>{{ $t("COMMUNICATION.TOOL.POPOVER_NOTICE") }}</p>
      <div
        class="cursor-pointer text-primary"
        @click="communicationToolOptions.showAsPopover = false"
      >
        {{ $t("COMMUNICATION.TOOL.RESTORE_TOOL") }}
      </div>
    </div>
    <template v-else>
      <div
        data-cy="communication-tool-static"
        class="flex items-center justify-between"
      >
        <div class="text-headline">{{ $t("COMMON.COMMUNICATION") }}</div>
        <div
          v-tooltip="$t('COMMON.SHOW_AS_POPOVER')"
          data-cy="init-popover"
          class="cursor-pointer px-1"
          @click="initPopover"
        >
          <icon-base :icon="IconPopup" />
        </div>
      </div>
      <div
        class="group grid grid-cols-4 items-center gap-2 border-t border-divider pt-2"
        data-cy="call-row"
      >
        <call-client-component
          :can-call="!!contactName"
          :telephone-number="telephoneNumber"
          @call="handleCallClient"
        />

        <div class="col-span-2 flex items-center space-x-2">
          <span>
            {{ $t("COMMON.CALL") }}
          </span>
          <span class="hidden group-hover:flex">
            <icon-base
              v-tooltip="`${$t('COMMON.NEW')} ${$t('DEALS.NOTES.CALL_NOTE')}`"
              data-cy="call-note"
              :icon="IconNotes"
              class="cursor-pointer text-green-500"
              @click="
                handleShowCommunicationModal($event, CommunicationType.call)
              "
            />
          </span>
        </div>
        <refresh-voice-token
          :provider="preferredDialer ?? DEFAULT_DIALER"
          @ring-central:reset="resetRingCentral"
        />
      </div>
      <template v-if="showCommunicationItems">
        <div
          class="group grid cursor-pointer grid-cols-4 items-center gap-2"
          @click="handleShowCommunicationModal($event, CommunicationType.sms)"
        >
          <div
            data-cy="sms-link"
            class="email-link-icon flex h-6-5 w-6-5 items-center justify-center rounded-full bg-turquoise/10 group-hover:bg-turquoise/20"
          >
            <icon-base :icon="IconMessage" class="text-secondary-blue" />
          </div>
          <div class="col-span-2">
            {{ $t("COMMON.SEND") }} {{ $t("COMMON.SMS") }}
          </div>
        </div>
        <div
          class="group grid cursor-pointer grid-cols-4 items-center gap-2"
          @click="handleShowCommunicationModal($event, CommunicationType.email)"
        >
          <div
            data-cy="email-link"
            class="email-link-icon flex h-6-5 w-6-5 items-center justify-center rounded-full bg-turquoise/10 group-hover:bg-turquoise/20"
          >
            <icon-base :icon="IconEmail" class="text-turquoise" />
          </div>
          <div class="col-span-2">
            {{ $t("COMMON.SEND") }} {{ $t("COMMON.EMAIL") }}
          </div>
        </div>
      </template>
      <div
        v-if="canCreateTasks"
        class="group grid cursor-pointer grid-cols-4 items-center gap-2"
        @click="handleShowCommunicationModal($event, CommunicationType.task)"
      >
        <div
          data-cy="task-link"
          class="task-link-icon flex h-6-5 w-6-5 items-center justify-center rounded-full bg-flush-orange/10 group-hover:bg-flush-orange/20"
        >
          <icon-base :icon="IconNotes" class="text-flush-orange" />
        </div>
        <div class="col-span-2">
          {{ $t("COMMON.NEW") }} {{ $t("COMMON.TASK") }}
        </div>
      </div>
      <tooltip
        v-if="hasActiveTemplateCustomWebhooks"
        ref="webhooksTooltip"
        placement="right-start"
        trigger="click"
        :arrow="false"
        theme="none"
        interactive
        :popper-options="webhooksPopperOptions"
      >
        <template #default>
          <div class="group grid cursor-pointer grid-cols-4 items-center gap-2">
            <div
              data-cy="webhook-button"
              class="task-link-icon flex h-[26px] w-[26px] items-center justify-center rounded-full bg-secondary-yellow/10 group-hover:bg-secondary-yellow/20"
            >
              <icon-base :icon="IconWebhook" class="text-secondary-yellow" />
            </div>
            <div class="col-span-2 text-nowrap">
              {{ $t("ORCHESTRATION.TRIGGER") }} {{ $t("COMMON.WEBHOOK") }}
            </div>
          </div>
        </template>
        <template #content>
          <custom-webhooks-list
            :app-id="activeDeal.id"
            :items="activeTemplateCustomWebhooks"
            ignore-placement
          />
        </template>
      </tooltip>
      <tooltip
        ref="contactsTooltipStatic"
        placement="top-start"
        trigger="click"
        :arrow="false"
        sticky="reference"
        max-width="450"
        theme="none"
        interactive
        :popper-options="popperOptions"
      >
        <template #default>
          <lf-pill
            data-cy="toggle-modal"
            size="sm"
            class="cursor-pointer font-bold uppercase"
            color-index="15"
          >
            <div class="flex items-center space-x-2">
              <div data-cy="contact-name" class="w-30 truncate text-left">
                <span v-if="contactName">{{ contactName }}</span>
                <span v-else>
                  {{ $t("ACTIVITY.CONTACTS.NO_CONTACT") }}
                </span>
              </div>
              <icon-chevron-up
                class="transform cursor-default transition-all"
                :class="{
                  'rotate-180': !contactsTooltipStatic?.state.isVisible,
                }"
                fill="#9ca3af"
              />
            </div>
          </lf-pill>
        </template>
        <template #content>
          <contacts-modal
            v-if="activeDeal"
            :value="activeDeal as ApplicationWithContactData"
            ignore-placement
            @close-modal="contactsTooltipStatic?.hide()"
          />
        </template>
      </tooltip>
    </template>
  </div>
  <teleport v-if="communicationToolOptions.showAsPopover" to="#modals">
    <vue-resizable
      data-cy="communication-tool-draggable"
      class="z-101"
      drag-selector=".draggable"
      :width="draggableToolWidth"
      :height="50"
      :top="communicationToolOptions.y"
      :left="communicationToolOptions.x"
      :active="[]"
      @drag:end="updatePosition"
    >
      <div
        ref="draggable"
        class="draggable flex h-[50px] items-center space-x-4 rounded-full border border-disabled/10 bg-grey-navigation px-2 text-white shadow-md"
      >
        <icon-base
          :icon="IconCardView"
          width="16"
          height="16"
          view-box="0 0 24 24"
          class="flex-none rotate-90 transform cursor-grab"
        />
        <div class="flex items-center gap-x-2">
          <communication-profile />
          <component
            :is="COMPONENT_MAP[item.name].component"
            v-for="item in pinnedItemsSorted"
            :key="item.name"
            :data-cy="item.name"
            @modal:show="handleShowCommunicationModal"
            @client:call="handleCallClient"
            @ring-central:reset="resetRingCentral"
          />
          <div
            v-tooltip="$t('COMMON.MORE')"
            class="modal-icon relative"
            data-cy="pinnable-menu"
            @click.stop="showPinnableMenu = !showPinnableMenu"
          >
            <div class="flex h-full items-center justify-center">
              <icon-base
                :icon="IconVerticalDots"
                width="22"
                height="22"
                view-box="0 0 24 24"
              />
              <div
                v-if="showPinnableMenu"
                ref="pinnableMenu"
                class="pinnable-menu"
                :style="pinnableStyle"
                @click.stop.prevent
              >
                <ul class="max-h-[327px] space-y-3 overflow-auto p-2">
                  <li
                    v-for="item in pinnableMenuOptions"
                    :key="item.name"
                    class="cursor-pointer truncate p-2 whitespace-nowrap text-headline"
                  >
                    <div
                      class="flex h-full items-center justify-between space-x-3"
                    >
                      <div class="flex items-center justify-start space-x-3">
                        <icon-base
                          :icon="item.icon"
                          :view-box="item.viewBox"
                          class="text-label"
                        />
                        <div>{{ item.label }}</div>
                      </div>
                      <icon-base
                        :icon="IconPinSecondary"
                        :data-cy="`pin-${item.name}`"
                        height="16"
                        width="16"
                        view-box="0 0 12 12"
                        class="shrink-0 cursor-pointer"
                        :class="item.pinned ? 'text-primary' : 'text-label'"
                        @click="togglePinnedItem(item.name)"
                      />
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="flex items-center space-x-1">
          <div class="text-xxs font-semibold text-disabled uppercase">
            {{ $t("COMMON.CONTACT") }}:
          </div>
          <tooltip
            ref="contactsTooltip"
            placement="bottom-start"
            trigger="click"
            :arrow="false"
            sticky="reference"
            max-width="450"
            theme="none"
            interactive
            :popper-options="popperOptions"
          >
            <template #default>
              <lf-pill
                data-cy="toggle-modal"
                class="cursor-pointer font-bold uppercase"
                color-index="15"
                opaque
              >
                <div class="flex items-center space-x-2">
                  <div data-cy="contact-name" class="w-30 truncate text-left">
                    <span v-if="contactName">{{ contactName }}</span>
                    <span v-else>
                      {{ $t("ACTIVITY.CONTACTS.NO_CONTACT") }}
                    </span>
                  </div>
                  <icon-chevron-up
                    class="transform cursor-default transition-all"
                    :class="{ 'rotate-180': !contactsTooltip?.state.isVisible }"
                    fill="#ffffff"
                  />
                </div>
              </lf-pill>
            </template>
            <template #content>
              <contacts-modal
                v-if="activeDeal"
                data-cy="contacts-modal-draggable"
                :value="activeDeal as ApplicationWithContactData"
                ignore-placement
              />
            </template>
          </tooltip>
        </div>
        <div
          v-tooltip="$t('COMMUNICATION.TOOL.CLOSE_POPOVER')"
          data-cy="close-popover"
          class="cursor-pointer px-1"
          @click="closePopoverAndClearPosition"
        >
          <icon-base :icon="IconPopup" class="rotate-180 transform" />
        </div>
      </div>
    </vue-resizable>
  </teleport>
  <teleport v-if="callDialog" to="#modals">
    <communication-log-call-dialog
      class="fixed z-50 shadow-lg"
      :value="callDialog"
      @close-dialog="callDialog = null"
    />
  </teleport>
  <send-deal-communication-modal
    v-if="showCommunicationModal"
    :type="modalType"
    :deal-id="activeDeal.id"
    :deal-name="activeDeal.business.business_legal_name"
    anchor-bottom-left
    @close-dialog="showCommunicationModal = false"
  />
</template>

<script setup lang="ts">
import {
  type Component,
  type Ref,
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
} from "vue";
import { useI18n } from "vue-i18n";
import VueResizable from "vue-resizable";
import { useStore } from "vuex";

import throttle from "lodash/throttle";

import { AnalyticsEvent } from "@/enums/analytics";
import {
  CallErrorReason,
  CommunicationToolItemType,
  CommunicationType,
} from "@/enums/communicationLogs";
import { CommunicationProvider } from "@/enums/communicationProviders";
import { ShortcutAction } from "@/enums/keyboardShortcuts";
import { setActiveCallSession } from "@/helpers/communication";
import { DEFAULT_DIALER } from "@/helpers/constants";
import { DEFAULT_PINNED_COMMUNICATION_TOOL_ITEMS } from "@/helpers/constants/deals";
import { TWILIO_PIP_OPEN } from "@/helpers/constants/events";
import { ensurePositionWithinViewport } from "@/helpers/elements";
import { useAuth } from "@/hooks/auth";
import { useEmitter, usePromiseWrapper } from "@/hooks/common";
import { useActivityPermissions } from "@/hooks/communicationLogs";
import { useTwilio } from "@/hooks/communicationProviders";
import { useContacts } from "@/hooks/contacts";
import { useDeals } from "@/hooks/deals";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import { useNotification } from "@/hooks/notifications";
import { useLocalStorageSetting } from "@/hooks/options";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";
import { dialerApi } from "@/services/dialer-api";
import usersApiService from "@/services/modules/users";
import { useAnalyticsStore } from "@/stores/analytics";
import { useCallsStore } from "@/stores/calls";

import CallClientComponent from "@/components/communication/CallClient.vue";
import CommunicationProfile from "@/components/communication/CommunicationProfile.vue";
import CustomWebhooksList from "@/components/communication/CustomWebhooksList.vue";
import SendDealCommunicationModal from "@/components/communication/SendDealCommunicationModal.vue";
import Call from "@/components/communication/communicationTypes/Call.vue";
import CallNote from "@/components/communication/communicationTypes/CallNote.vue";
import Email from "@/components/communication/communicationTypes/Email.vue";
import RefreshVoiceToken from "@/components/communication/communicationTypes/RefreshVoiceToken.vue";
import Sms from "@/components/communication/communicationTypes/Sms.vue";
import Task from "@/components/communication/communicationTypes/Task.vue";
import Webhook from "@/components/communication/communicationTypes/Webhook.vue";
import CommunicationLogCallDialog from "@/components/communicationLogs/communication/CommunicationLogCallDialog.vue";
import IconCardView from "@/components/icons/IconCardView.vue";
import IconEmail from "@/components/icons/IconEmail.vue";
import IconMessage from "@/components/icons/IconMessage.vue";
import IconNotes from "@/components/icons/IconNotes.vue";
import IconPinSecondary from "@/components/icons/IconPinSecondary.vue";
import IconPopup from "@/components/icons/IconPopup.vue";
import IconReload from "@/components/icons/IconReload.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import IconVerticalDots from "@/components/icons/IconVerticalDots.vue";
import IconWebhook from "@/components/icons/IconWebhook.vue";
import IconBase from "@/components/ui/IconBase.vue";
import ContactsModal from "@/views/deals/components/contacts/ContactsModal.vue";

import type { ApplicationWithContactData } from "@/models/applications";
import type { ResizableEventValues } from "@/models/common";
import type {
  CallLog,
  CommunicationModalType,
} from "@/models/communicationLogs";
import type { IUser } from "@/models/users";
import type { TippyComponent } from "vue-tippy";

const ICON_WIDTH = 32;
const ICON_SPACING = 8;
// all other elements
// 32px + 16 grab + 60 profile + 16 space-x + 32 'more' icon + 16 space-x + 220 contact + 16 space-x + 24 close icon
const STATIC_WIDTH = 432;

type CommuncationToolItemOptions = {
  component: Component;
  icon: Component;
  viewBox?: string;
  sort: number;
  isCommunicationItem?: boolean;
};

const COMPONENT_MAP: Record<
  CommunicationToolItemType,
  CommuncationToolItemOptions
> = {
  [CommunicationToolItemType.call]: {
    component: Call,
    icon: IconTelephone,
    sort: 1,
    isCommunicationItem: false,
  },
  [CommunicationToolItemType.call_note]: {
    component: CallNote,
    icon: IconTelephone,
    sort: 2,
    isCommunicationItem: false,
  },
  [CommunicationToolItemType.sms]: {
    component: Sms,
    icon: IconMessage,
    sort: 3,
    isCommunicationItem: true,
  },
  [CommunicationToolItemType.email]: {
    component: Email,
    icon: IconEmail,
    sort: 4,
    isCommunicationItem: true,
  },
  [CommunicationToolItemType.task]: {
    component: Task,
    icon: IconNotes,
    sort: 5,
    isCommunicationItem: false,
  },
  [CommunicationToolItemType.webhook]: {
    component: Webhook,
    icon: IconWebhook,
    sort: 6,
    isCommunicationItem: false,
  },
  [CommunicationToolItemType.reset_ring_central]: {
    component: RefreshVoiceToken,
    icon: IconReload,
    viewBox: "4 4 16 16",
    sort: 7,
    isCommunicationItem: false,
  },
};

const { activeDeal } = useDeals();
const { isLendflowUser } = useAuth();
const { assignShortcutToAction } = useKeyboardShortcuts();
const { trackEvent } = useAnalyticsStore();
const { preferredDialer } = useTwilio();
const callStore = useCallsStore();
const emitter = useEmitter();


const { telephoneNumber, contactName, callClient } = useContacts(
  activeDeal as unknown as Ref<ApplicationWithContactData>
);

const { activeTemplateCustomWebhooks } = useActiveWorkflowTemplate();

const { getters } = useStore();
const communicationToolOptions = useLocalStorageSetting(
  "communicationToolOptions",
  {
    x: 0,
    y: 0,
    showAsPopover: false,
  }
);

const { t } = useI18n();
const { showMessage } = useNotification();

const { canCreateTasks } = useActivityPermissions();

const pinnedItems = useLocalStorageSetting(
  "pinnedCommunicationToolItems",
  DEFAULT_PINNED_COMMUNICATION_TOOL_ITEMS
);

const pinnedItemsSorted = computed(() => {
  return [...pinnedItems.value]
    .sort((a, b) => a.sort - b.sort)
    .filter((item) => {
      if (item.name === CommunicationToolItemType.task) {
        return canCreateTasks.value;
      }
      return (
        ((showCommunicationItems.value && item.isCommunicationItem) ||
          !item.isCommunicationItem) &&
        (item.name === CommunicationToolItemType.webhook
          ? hasActiveTemplateCustomWebhooks.value
          : true)
      );
    });
});

const pinnableMenuOptions = computed(() => {
  return Object.values(CommunicationToolItemType)
    .map((item) => {
      return {
        label: t(`COMMUNICATION.PINNABLE.${item.toUpperCase()}`),
        name: item,
        icon: COMPONENT_MAP[item].icon,
        viewBox: COMPONENT_MAP[item].viewBox,
        isCommunicationItem: COMPONENT_MAP[item].isCommunicationItem,
        pinned: pinnedItems.value.find((pinnedItem) => pinnedItem.name === item)
          ? true
          : false,
      };
    })
    .filter(({ name, isCommunicationItem }) => {
      if (name === CommunicationToolItemType.task) {
        return canCreateTasks.value;
      }
      return (
        (showCommunicationItems.value && isCommunicationItem) ||
        !isCommunicationItem
      );
    });
});
const callDialog = ref<CallLog | null>(null);
const showPinnableMenu = ref(false);
const showCommunicationModal = ref(false);
const modalType = ref<CommunicationModalType>(CommunicationType.email);
const contactsTooltip = ref<TippyComponent | null>(null);
const contactsTooltipStatic = ref<TippyComponent | null>(null);
const popperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: { fallbackPlacements: ["bottom", "top"] },
    },
  ],
});
const webhooksPopperOptions = ref<TippyComponent["popperOptions"]>({
  modifiers: [
    {
      name: "flip",
      options: {
        fallbackPlacements: ["left-start", "bottom-start", "top-start"],
      },
    },
    {
      name: "preventOverflow",
      options: { boundary: "viewport" },
    },
  ],
});
const webhooksTooltip = ref<TippyComponent | null>(null);
const draggable = ref<HTMLElement | null>(null);
const pinnableMenu = ref<HTMLElement | null>(null);
const { loading: isResettingRingCentral, fetchWrapper: resetRingCentral } =
  usePromiseWrapper(async () => {
    if (!currentUser.value?.id) {
      return;
    }

    await usersApiService.resetRingCentral(currentUser.value.id);
  });

const hasActiveTemplateCustomWebhooks = computed(
  () => !!Object.keys(activeTemplateCustomWebhooks.value).length
);

const draggableToolWidth = computed(() => {
  const variableWidth =
    pinnedItemsSorted.value.length * (ICON_SPACING + ICON_WIDTH);
  return variableWidth + STATIC_WIDTH;
});

const pinnableStyle = computed(() => {
  if (!pinnableMenu.value) {
    return {};
  }
  const pinnableMenuBounding = pinnableMenu.value.getBoundingClientRect();
  if (communicationToolOptions.value.y <= pinnableMenuBounding.height) {
    return {
      left: "0px",
      top: "45px",
    };
  }
  return {
    left: "0px",
    bottom: "45px",
  };
});

const currentUser = computed<IUser>(() => getters["auth/user"]);
const showCommunicationItems = computed(
  () => activeDeal.value.workflow_name && isLendflowUser
);

const togglePinnedItem = (item: CommunicationToolItemType) => {
  const itemIndex = pinnedItems.value.findIndex(
    (pinnedItem) => pinnedItem.name === item
  );
  const option = COMPONENT_MAP[item];
  if (itemIndex !== -1) {
    pinnedItems.value = pinnedItems.value.filter(
      (_, index) => index !== itemIndex
    );
  } else {
    pinnedItems.value = [
      ...pinnedItems.value,
      {
        name: item,
        sort: option.sort,
        isCommunicationItem: option.isCommunicationItem,
      },
    ];
  }
};

const handleCallClient = async (e?: MouseEvent) => {
  if (isResettingRingCentral.value || !telephoneNumber.value) {
    if (!telephoneNumber.value) {
      trackEvent(AnalyticsEvent.CallOutError, {
        reason: CallErrorReason.MissingPhone,
      });
      showMessage(t("TWILIO.NO_PHONE_NUMBER"), "error");
    }
    return;
  }
  const result = await callClient(e);
  if (!result?.connectionLog?.id) {
    trackEvent(AnalyticsEvent.CallOutError, {
      reason: CallErrorReason.MissingApiResponse,
    });
    showMessage(t("TWILIO.CALL_OUT_ERROR"), "error");
    return;
  }
  setActiveCallSession(result);

  trackEvent(AnalyticsEvent.CommunicationCallInitiated, {
    provider:
      result.callOutPayload?.provider ??
      preferredDialer.value ??
      DEFAULT_DIALER,
    deal_id: activeDeal.value?.id,
  });

  // Auto-open PiP for Twilio calls only when this browser is the active dialer
  if (
    result.callOutPayload?.provider === CommunicationProvider.Twilio &&
    !callStore.isPiPOpen &&
    dialerApi.isActiveDialer.value
  ) {
    emitter.emit(TWILIO_PIP_OPEN);
  }

  if (result.event) {
    communicationToolOptions.value.x = result.event.x;
    communicationToolOptions.value.y = result.event.y;
  }
  callDialog.value = result.connectionLog;
};

const initPopover = async (event: MouseEvent) => {
  communicationToolOptions.value = {
    x: communicationToolOptions.value?.x || event.x,
    y: communicationToolOptions.value?.y || event.y,
    showAsPopover: true,
  };
  nextTick(async () => await repositionAsNeeded());
};

const updatePosition = async (position: ResizableEventValues) => {
  communicationToolOptions.value = {
    ...communicationToolOptions.value,
    x: position.left,
    y: position.top,
  };
  await repositionAsNeeded();
};

const repositionAsNeeded = async () => {
  const draggableBounding = draggable.value?.getBoundingClientRect();
  const updates = await ensurePositionWithinViewport(
    communicationToolOptions.value,
    draggableBounding
  );
  communicationToolOptions.value = {
    ...communicationToolOptions.value,
    x: updates.x,
    y: updates.y,
  };
};

const closePopoverAndClearPosition = () => {
  communicationToolOptions.value = { x: 0, y: 0, showAsPopover: false };
  showPinnableMenu.value = false;
};

const handleShowCommunicationModal = (
  e: MouseEvent,
  type: CommunicationModalType
) => {
  if (!e.target) {
    return;
  }
  showCommunicationModalType(type);
};

const showCommunicationModalType = (type: CommunicationModalType) => {
  showCommunicationModal.value = true;
  modalType.value = type;
  resizeListener();
};

const resizeListener = throttle(
  async () => {
    await repositionAsNeeded();
  },
  100,
  { leading: false }
);

assignShortcutToAction({ actionName: ShortcutAction.StartCall, action: handleCallClient });
assignShortcutToAction({ actionName: ShortcutAction.ComposeSms, action: () =>
  showCommunicationModalType(CommunicationType.sms)
});
assignShortcutToAction({ actionName: ShortcutAction.ComposeEmail, action: () =>
  showCommunicationModalType(CommunicationType.email)
});
assignShortcutToAction({ actionName: ShortcutAction.CreateTask, action: () =>
  showCommunicationModalType(CommunicationType.task)
});

onMounted(() => {
  window.addEventListener("resize", resizeListener);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", resizeListener);
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

:deep() .modal-icon {
  @apply h-[32px] w-[32px] cursor-pointer rounded-full text-white transition-colors duration-300 hover:bg-divider/10;
}

.pinnable-menu {
  @apply absolute z-100 max-h-100 min-h-30 max-w-145 min-w-80 rounded bg-white shadow-lg transition-all;
}
</style>
